import { batch } from 'react-redux';
import { EmeraldError } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { CardType } from 'src/common/enumerations/card-type';
import { EwicCaller } from 'src/common/enumerations/request-ewic-caller';
import { TenderType } from 'src/common/enumerations/tender-type';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { PaymentUsage, RefundUsage } from 'src/common/interfaces/dtos/IPreTenderAdd';
import { AuthorizationStatus } from 'src/common/enumerations/authorization-status';
import { ItemListActions } from 'src/common/redux/reducers/item-list-reducer';
import {
  CardDetails,
  EpsData,
  parseElectronicPaymentData,
  PaymentMessage,
} from 'src/devices/payment/dtos/eps-data';
import { resetCardData } from 'src/devices/payment/get-card-details';
import {
  beginPaymentSession,
  completePrescriptionFromPinPad,
  endPaymentSession,
  getPrescriptionFromPinPad,
  inPaymentSession,
  purchaseUsingPinPad,
  returnUsingPinPad,
  voidPayment,
} from 'src/devices/payment/pinpad-operations';
import { cancelTenderPayment } from 'src/home-page/business-logic/cancel-tender-payment';
import { makeTenderPayment } from 'src/home-page/business-logic/make-tender-payment';
import { processCashback } from 'src/tendering/business-logic/process-cashback';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { convertCurrency } from '../../common/helpers/currency-helper';
import { ITender } from '../../common/interfaces/dtos/ITender';
import { store } from '../../common/redux/configure-store';
import { PinPadActions } from '../../common/redux/reducers/pinpad-reducer';
import { TenderingActions } from '../../common/redux/reducers/tendering-reducer';
import { preMakeTenderPayment } from '../../home-page/business-logic/pre-make-tender-payment';
import { buildRxDto } from './build-complete-ewic-rx';
import { BuildLineTenderProps, buildTenderAddDTO } from './build-line-tender';
import { decodeRx } from './decode-rx';
import { getTenderByEpsData } from './get-tender-by-eps-data';
import { validateAlternateTenderRequired } from './handle-ewic-eligibility-amount';
import { processSignature } from './handle-signature-selection';
import { setSelectedTenderAndGroup } from './handle-tender-payment';
import { processPartialTender } from './process-partial-tender';
import { isEwic, processPreAddTenderEwic } from './process-ewic-tender';
import { performTPSAction, TPSActions, TPSStatus } from './services/third-party-session';
import { EpsPaymentFailure, SignatureNotVerified } from 'src/common/helpers/error-code';
import { PaymentException } from 'src/common/exceptions/paymentException';

export interface ProcessEFTProps {
  amount: number;
  amountDue: number;
  epsData: EpsData;
  tender?: ITender;
  usage?: PaymentUsage | RefundUsage;
  cashbackAmount?: number;
}

export const processEFTTender = async (props: ProcessEFTProps): Promise<void> => {
  const { requiresTPSCallsForEft, requiresTPSCallsForEwic } = getCurrentXLRConfig();
  const { tender, usage } = props;
  let amount = props.amount;
  let { amountDue, epsData } = props;
  let pretenderAmount = amount;

  let shouldVoidOnException: boolean = false;
  const currentSelectedTender = store.getState().tenderMenu.selectedTender;
  let purchaseResponse: PaymentMessage = {};
  let expDate = '';
  let isEwicCard: boolean = false;
  const isTenderExchange =
    usage === PaymentUsage.ExchangePay || usage === PaymentUsage.ExchangeReceive;
  const isRefund = usage === RefundUsage.Refund || usage === PaymentUsage.ExchangeReceive;
  const callGetCardDetailsByTenderType = store.getState().touchPoint.touchPointConfiguration
    .callGetCardDetailsByTenderType;
  let tenderProps: BuildLineTenderProps | undefined = undefined;
  try {
    tenderProps = {
      tender:
        tender && tender.description === epsData?.card.cardName
          ? tender
          : getTenderByEpsData(epsData),
      amount,
      lineItemEntryMethod: EntryMethod.Empty,
      usage,
    };

    if (tenderProps?.tender !== currentSelectedTender) {
      setSelectedTenderAndGroup(tenderProps?.tender);
    }

    store.dispatch(
      TenderingActions.setPaymentInfoText(
        'EFTTenderProcessingCardPrompt',
        convertCurrency(amount),
        epsData.card.cardType ?? '',
      ),
    );

    // todo handle ewic partial payment

    isEwicCard = isEwic(tenderProps.tender, epsData);
    if (isEwicCard) {
      tenderProps.tender.type = TenderType.Ewic;
      const getPrescription = await getPrescriptionFromPinPad(epsData.paymentTranID);
      await sendPreTenderInitAndCancel(tenderProps, epsData);

      const beginningRx = await decodeRx(epsData, getPrescription);
      tenderProps.ewicEpsPrescriptionData = {
        initialItemPrescriptions: [...beginningRx.prescriptions],
      };
      expDate = beginningRx.expirationDate;

      const cardCollection = getPrescription?.cardDetails ?? [];
      const previousCard: CardDetails = cardCollection[cardCollection?.length - 1];

      tenderProps.lineItemEntryMethod = EntryMethod.Automatic;
      tenderProps.amount = amount;
      tenderProps.ewicEpsPrescriptionData = {
        ewicData: getPrescription.ewicData,
        responseCode: getPrescription.paymentDetails?.responseCode,
        stateIdentifierCode: previousCard.stateCode,
        pan: `${previousCard.firstSix}${'*'.repeat((previousCard.cardLength ?? 10) - 10)}${
          previousCard.lastFour
        }`,
        authorizationCode: getPrescription.paymentDetails?.authCode,
        enabledEpsData: true,
        enabledCancel: false,
        initialItemPrescriptions: tenderProps.ewicEpsPrescriptionData?.initialItemPrescriptions,
      };

      await processPreAddTenderEwic(tenderProps, EwicCaller.PretenderAdd, epsData);

      await performTPSStart(requiresTPSCallsForEwic, amountDue, pretenderAmount, tenderProps);

      const completeEwicRxData = await buildRxDto(getPrescription, epsData);
      if (completeEwicRxData.tenderedAmount) {
        purchaseResponse = {
          ...purchaseResponse,
          ...(await completePrescriptionFromPinPad(completeEwicRxData)),
        };
        tenderProps.amount = completeEwicRxData.tenderedAmount;
        const finalRx = await decodeRx(epsData, purchaseResponse);
        tenderProps.ewicEpsPrescriptionData.finalItemPrescriptions = finalRx.prescriptions;

        if (purchaseResponse.paymentDetails) {
          purchaseResponse.paymentDetails.paymentTranID = epsData.paymentTranID;
        }
        if (purchaseResponse.amounts?.amountApproved) {
          tenderProps.amount = +purchaseResponse.amounts.amountApproved;
          const amountApproved = getValueAsNumber(purchaseResponse.amounts.amountApproved);
          await validateAlternateTenderRequired(amountApproved, amountDue);
        }
      }
    } else {
      const buildLineProps: BuildLineTenderProps = {
        tender:
          tender && tender.description === epsData?.card.cardName
            ? tender
            : getTenderByEpsData(epsData),
        amount,
        lineItemEntryMethod: EntryMethod.Empty,
        usage,
      };
      const lineTender = buildTenderAddDTO(buildLineProps);
      await preMakeTenderPayment(lineTender);

      /* unpaidPretender amount in store can change when preMakeTenderPayment
      call handles tender eligibility 
      exceptions, need to update the amounts
      */
      const { unPaidPreTender } = store.getState().tenderMenu;
      const newAmountForEligibility =
        unPaidPreTender && unPaidPreTender.amount?.amount ? unPaidPreTender.amount?.amount : amount;

      /* TODO : here amount changes since the pre-add can come back with a new amount - 
      like for tax exemption purposes, but we need to try not to change this if we can */
      store.dispatch(ItemListActions.setAmountDue(amountDue));
      amount = newAmountForEligibility;

      // show new amount due for this tender in the processing view
      pretenderAmount = newAmountForEligibility || pretenderAmount;
      buildLineProps.amount = newAmountForEligibility;

      await performTPSStart(!isRefund, amountDue, pretenderAmount, buildLineProps);

      if (isRefund) {
        purchaseResponse = {
          ...purchaseResponse,
          ...(await returnUsingPinPad(amount, amountDue, epsData.paymentTranID)),
        };
      } else {
        purchaseResponse = {
          ...purchaseResponse,
          ...(await purchaseUsingPinPad(
            props.amount,
            props.amountDue,
            epsData.paymentTranID,
            props.cashbackAmount,
          )),
        };
      }
    }
    // All error after succeed pinpad operation needs void the pinpad payment
    shouldVoidOnException = true;

    await processCashback(purchaseResponse);

    await processPartialTender(purchaseResponse, isEBTCard(epsData));

    epsData = parseElectronicPaymentData(purchaseResponse);
    if (callGetCardDetailsByTenderType && epsData.card) {
      //with EmeraldGX we use card details from purchase response.
      tenderProps.tender = getTenderByEpsData(epsData);
    }

    store.dispatch(PinPadActions.setPaymentTransactionId(epsData.paymentTranID));
    const lineTenderAfterPayment = buildTenderAddDTO({
      ...tenderProps,
      payment: purchaseResponse,
    });
    amountDue = epsData.amountApproved && !isTenderExchange ? +epsData.amountApproved : amountDue;

    if ((requiresTPSCallsForEft && !isEwicCard) || (requiresTPSCallsForEwic && isEwicCard)) {
      await performTPSAction(amountDue, pretenderAmount, TPSActions.Update);
    }
    if (!isRefund) {
      store.dispatch(TenderingActions.setPaymentInfoText('EFTTenderCardApprovedPrompt'));
    } else {
      store.dispatch(TenderingActions.setPaymentInfoText('EFTTenderRefundApprovedPrompt'));
    }

    if ((requiresTPSCallsForEft && !isEwicCard) || (requiresTPSCallsForEwic && isEwicCard)) {
      await performTPSAction(amountDue, pretenderAmount, TPSActions.Finish);
    }
    if (purchaseResponse?.paymentDetails?.needsSignature) {
      await processSignature(tenderProps, epsData);
    }

    if (lineTenderAfterPayment.epsData) {
      lineTenderAfterPayment.epsData.thirdPartySessionId = store.getState().pinPad.thirdPartyData?.sessionId;
      if (isEwicCard) {
        lineTenderAfterPayment.epsData.card = {
          ...lineTenderAfterPayment.epsData.card,
          extendedData: { expirationDate: expDate },
        };
      }
    }

    await endPaymentSession();
    await makeTenderPayment(lineTenderAfterPayment);
  } catch (err) {
    if (err instanceof PaymentException) {
      const data = err as PaymentException;
      if (
        (data.code === EpsPaymentFailure || data.code == SignatureNotVerified) &&
        data.paymentMessage &&
        tenderProps
      ) {
        await processDeclinedPayment(data.paymentMessage, tenderProps, amountDue);
      }
    }
    await onProcessEFTError(epsData, pretenderAmount, shouldVoidOnException, isRefund);
    throw err;
  } finally {
    batch(() => {
      resetCardData();
      store.dispatch(PinPadActions.resetThirdPartyData());
    });
  }
};

export const onProcessEFTError = async (
  paymentData: EpsData,
  pretenderAmount: number,
  shouldVoidOnException: boolean,
  isRefund: boolean,
): Promise<void> => {
  // TODO: Figure out how to stop voiding the payment twice
  const { requiresCancelPreTenderFromCart } = getCurrentXLRConfig();
  await withSpinner(async () => {
    if (shouldVoidOnException) {
      if (inPaymentSession()) {
        await endPaymentSession();
      }
      await beginPaymentSession();
      await voidPayment(paymentData.paymentTranID, paymentData.sessionTranID);
    }
    if (requiresCancelPreTenderFromCart) {
      const lineId = store.getState().tenderMenu.unPaidPreTender?.lineId;
      if (lineId) {
        await cancelTenderPayment(lineId, isRefund, true);
      }
    }

    const amountDue = paymentData.amountApproved ? +paymentData.amountApproved : pretenderAmount;
    const thirdPartyData = store.getState().pinPad.thirdPartyData;
    const transactionId = store.getState().transaction.transactionId;
    if (thirdPartyData && thirdPartyData.sessionId) {
      await performTPSAction(amountDue, pretenderAmount, TPSActions.Cancel, {
        transactionId,
        ...thirdPartyData,
      });
    }
    if (inPaymentSession()) {
      await endPaymentSession();
    }
  });
};

export const processDeclinedPayment = async (
  paymentMessage: PaymentMessage,
  tenderProps: BuildLineTenderProps,
  amountDue: number,
): Promise<void> => {
  const lineTenderDeclinedPayment = buildTenderAddDTO({
    ...tenderProps,
    payment: paymentMessage,
  });

  if (lineTenderDeclinedPayment.epsData) {
    lineTenderDeclinedPayment.epsData.authorization = {
      authorizationCode: AuthorizationStatus.Decline,
      authorizationMethod: lineTenderDeclinedPayment.amount?.amountEntryMethod,
      referenceNumber: paymentMessage.paymentDetails?.paymentTranID,
    };

    const cardDetails = paymentMessage?.cardDetails ? paymentMessage?.cardDetails[0] : {};

    lineTenderDeclinedPayment.epsData.card = {
      ...lineTenderDeclinedPayment.epsData.card,
      cardType: cardDetails?.cardType,
    };

    lineTenderDeclinedPayment.amount = {
      ...lineTenderDeclinedPayment.amount,
      requestedAmount: lineTenderDeclinedPayment?.amount?.requestedAmount || amountDue,
    };

    lineTenderDeclinedPayment.epsData.thirdPartySessionId = store.getState().pinPad.thirdPartyData?.sessionId;

    await makeTenderPayment(lineTenderDeclinedPayment).catch(err => {
      /*with R10 the tender add is the one to throw the decline exception and not the RPS  */
      if (err.code !== 'EPSAuthorizationDeclined') {
        throw err;
      }
    });
  }
};

const isEBTCard = (epsData: EpsData): boolean => {
  return (
    epsData.card.cardType?.toUpperCase() === CardType.Ebtcash ||
    epsData.card.cardType?.toUpperCase() === CardType.Ebtfoodstamp
  );
};

export const sendPreTenderInitAndCancel = async (
  buildLineProps: BuildLineTenderProps,
  epsData: EpsData,
): Promise<void> => {
  const { requiresPreTenderInitAndCancel } = getCurrentXLRConfig();
  if (requiresPreTenderInitAndCancel) {
    return processPreAddTenderEwic(buildLineProps, EwicCaller.Initial, epsData)
      .then(() => processPreAddTenderEwic(buildLineProps, EwicCaller.Cancel, epsData))
      .catch(() => {
        throw new EmeraldError('PretenderAddError');
      });
  }
};

export const performTPSStart = async (
  requiresTPS: boolean,
  amountDue: number,
  pretenderAmount: number,
  buildLineProps: BuildLineTenderProps,
): Promise<void> => {
  if (requiresTPS) {
    await performTPSAction(
      amountDue,
      pretenderAmount,
      TPSActions.Start,
      undefined,
      TPSStatus.PreTender,
      buildLineProps.tender?.id,
    );
  }
};
